import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type * as monaco from 'monaco-editor';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import DriveUnitPortControl from '../Devices/Controls/DriveUnitPortControl';
import type { PendingDeviceSnippetPortDrop } from '../../../utils/deviceSnippetPortDrop';
import { applyDeviceSnippetPortToCode } from '../../../utils/deviceSnippetPortDrop';
import { isDriveUnitPortTakenElsewhere } from '../../../utils/driveUnitPortConflict';
import { getFirstUnusedDriveUnitPort } from '../../../utils/driveUnitPortSelection';
import type { DeviceCardProps } from '../Devices/DeviceCardProps';

const SNIPPET_DROP_SYNTH_PREFIX = '__snippet_drop__';

const shouldConfirmFromEnter = (key: string, eventTarget: EventTarget | null) => {
  if (key !== 'Enter') return false;
  if (!(eventTarget instanceof Element)) return true;
  const activeButton = eventTarget.closest('button,[role="button"]');
  if (!activeButton) return true;

  const isSelectedPortButton = activeButton.getAttribute('role') === 'button' && activeButton.getAttribute('aria-pressed') === 'true';

  return isSelectedPortButton;
};

interface DeviceSnippetPortDialogProps {
  open: boolean;
  onClose: () => void;
  pending: (PendingDeviceSnippetPortDrop & { targetLine: number }) | null;
  editor?: monaco.editor.IStandaloneCodeEditor | null;
  driveUnitPortRegistryRev?: number;
  onConfirm: (filledCode: string) => void;
}

const DeviceSnippetPortDialog = ({
  open,
  onClose,
  pending,
  editor = null,
  driveUnitPortRegistryRev = 0,
  onConfirm,
}: DeviceSnippetPortDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [port, setPort] = useState<number>(1);
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const lastSnippetElectronicRef = useRef<string | null>(null);
  const dialogPaperRef = useRef<HTMLDivElement | null>(null);
  const conflictMessage = t('portSelector.conflict.message', {
    defaultValue: 'This port is already used by another mechanism or electronic in your code.',
  });

  useEffect(() => {
    if (!open || !pending) return;
    const electronicKey = `${SNIPPET_DROP_SYNTH_PREFIX}:${pending.variableName}:${pending.parameterName}`;
    setPort(getFirstUnusedDriveUnitPort(driveUnitPorts, { excludeElectronic: electronicKey }));
    // Intentionally omit `driveUnitPorts`: only pick a default when this drop opens, not when the user changes port.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- driveUnitPorts read at open/pending change only
  }, [open, pending]);

  useEffect(() => {
    if (!open) {
      if (lastSnippetElectronicRef.current) {
        dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic: lastSnippetElectronicRef.current }));
        lastSnippetElectronicRef.current = null;
      }
      return;
    }
    if (!pending) return;
    const nextKey = `${SNIPPET_DROP_SYNTH_PREFIX}:${pending.variableName}:${pending.parameterName}`;
    const prev = lastSnippetElectronicRef.current;
    if (prev && prev !== nextKey) {
      dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic: prev }));
    }
    lastSnippetElectronicRef.current = nextKey;
  }, [open, pending, dispatch]);

  useEffect(() => {
    if (!open || !pending) return;
    const electronic = `${SNIPPET_DROP_SYNTH_PREFIX}:${pending.variableName}:${pending.parameterName}`;
    if (!Number.isFinite(port) || port < 1 || port > 8) {
      dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic }));
      return;
    }
    dispatch(applicationSlice.actions.onUpdateDriveUnitPorts({ driveUnitPort: port, electronic }));
  }, [dispatch, open, pending, port, driveUnitPortRegistryRev]);

  const electronicKeyBase = pending ? `${SNIPPET_DROP_SYNTH_PREFIX}:${pending.variableName}` : '';

  const occupiedInlineMessage = useMemo(() => {
    if (!pending || !Number.isFinite(port) || port < 1 || port > 8) return null;
    const electronicKey = `${electronicKeyBase}:${pending.parameterName}`;
    if (!isDriveUnitPortTakenElsewhere(driveUnitPorts, port, electronicKey)) return null;
    return conflictMessage;
  }, [pending, port, driveUnitPorts, electronicKeyBase, conflictMessage]);

  const deviceProps: DeviceCardProps | null = useMemo(() => {
    if (!pending) return null;
    return {
      id: electronicKeyBase,
      header: '',
      editor: editor ?? undefined,
      initialOpen: true,
      lineNumber: pending.targetLine,
      parameterName: pending.parameterName,
      parentName: pending.variableName,
      mechanismName: pending.variableName,
      mechanismClass: pending.mechanismClass,
      driveUnitPortRegistryRev,
    };
  }, [pending, editor, driveUnitPortRegistryRev, electronicKeyBase]);

  const handleConfirm = useCallback(() => {
    if (!pending) return;
    onConfirm(applyDeviceSnippetPortToCode(pending, port));
  }, [onConfirm, pending, port]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (!shouldConfirmFromEnter(event.key, event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    handleConfirm();
  };

  useEffect(() => {
    if (!open || !pending) return;

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.key !== 'Enter') return;
      const target = event.target instanceof Node ? event.target : null;
      if (target && dialogPaperRef.current?.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      handleConfirm();
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown, true);
  }, [handleConfirm, open, pending]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          ref: dialogPaperRef,
          onKeyDown: handleKeyDown,
          sx: {
            width: 'fit-content',
            maxWidth: 'calc(100% - 32px)',
          },
        },
      }}
    >
      <DialogTitle textAlign="center" sx={{ mx: '3rem', pt: 2, pb: 1 }}>
        {t('codeEditor.deviceSnippetPort.title', 'Set Port')}
      </DialogTitle>
      <DialogContent sx={{ m: '1rem', p: 0 }}>
        {pending && deviceProps && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '& .MuiInputLabel-root': {
                  display: 'block',
                  textAlign: 'center',
                  width: '100%',
                },
              }}
            >
              <DriveUnitPortControl
                {...deviceProps}
                portControlValue={port}
                setPortControlValue={setPort}
                skipEditorParameterSync
                portConflictPresentation="inline"
                labelId="snippet-drop-port-label"
                label={t('codeEditor.deviceSnippetPort.portLabel', 'Port')}
                selectId="snippet-drop-port"
                selectLabelId="snippet-drop-port-select-label"
              />
            </Box>
            {occupiedInlineMessage ? (
              <Typography variant="body2" color="text.warning" sx={{ mt: 1.5, mx: 'auto', width: 'fit-content', maxWidth: 404 }}>
                {occupiedInlineMessage}
              </Typography>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 2, pt: 2, pr: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
        <Button variant="contained" onClick={handleConfirm}>
          {t('codeEditor.deviceSnippetPort.confirm', 'Insert')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceSnippetPortDialog;
